import { Logger } from 'wdc-cube';
import { CompanyApi } from '../../modules/concrete/restricted/companies/companies.api';
import { CompanyOptionScope, CompanySelectorScope } from './company-selector.scope';
const LOG = Logger.get('Mormaii.CompanySelectorPresenter');
// @Inject
const companyApi = CompanyApi.INSTANCE;
/** Quantas empresas o seletor mostra de uma vez. */
const PAGE_SIZE = 20;
/**
 * Quanto tempo o campo espera parar de ser digitado antes de perguntar ao servidor.
 *
 * 250ms e o intervalo em que uma pessoa digitando devagar ainda nao terminou a
 * palavra. Sem ele, escrever "mormaii" seriam sete requisicoes.
 */
const DEBOUNCE_MS = 250;
/**
 * O seletor de empresa - um presenter SEM place, hospedado por tres telas.
 *
 * Existe porque a lista deixou de caber na memoria do navegador: o painel, o filtro
 * de Colaboradores e o da Adocao e Engajamento traziam TODAS as empresas na montagem e
 * desenhavam um botao para cada uma dentro de um `max-h-60 overflow-y-auto`. Com
 * algumas centenas de clientes isso vira baixar a base de empresas a cada tela
 * aberta, e escolher uma vira rolagem cega.
 *
 * Aqui a lista vem do servidor, uma pagina por vez, conforme se digita. Quem escolhe
 * continua guardando o ID num parametro de place - o seletor nao tem endereco, e nao
 * deveria ter: ele e um pedaco de tela, e o lugar e de quem o hospeda.
 *
 * O termo NAO vira parametro de URL de proposito. Ele e o caminho ate a escolha, e
 * nao a escolha; guardar "mor" no endereco nao ajuda ninguem a voltar.
 */
/** O aviso de quem chegou por um link a uma empresa que nao existe mais. */
export const MISSING_COMPANY = {
    title: 'Empresa não encontrada',
    text: 'O link apontava para uma empresa que não existe mais. Mostrando todas as empresas.'
};
export class CompanySelectorPresenter {
    /**
     * Nao ha `this.update()` depois de escrever no scope, de proposito.
     *
     * O campo `@observe()` ja o chama: o acessor instalado pelo `@Observable`
     * compara, guarda e avisa (`if (this[chave] !== novo) { ...; scope.update(scope) }`).
     * Chamar de novo so repetiria o aviso. `update` fica guardado aqui para o
     * CONSTRUTOR entrega-lo ao scope - e e o do hospedeiro, entao este pedaco de
     * tela anda no mesmo ciclo de atualizacao dele.
     *
     * Onde ainda seria preciso chamar: mudanca que o decorator NAO ve - mexer dentro
     * de um array ou de um objeto ja atribuido, em vez de atribuir um novo.
     *
     * @param update o `update` do presenter hospedeiro
     * @param onChoose chamado quando se escolhe; o hospedeiro escreve o endereco,
     *                 porque o parametro e dele
     * @param onMissing chamado quando a empresa do endereco NAO EXISTE - o link e
     *                  velho, ou veio de outra base. O seletor ja voltou para "todas"
     *                  pelo mesmo caminho do X; o hospedeiro so diz ao leitor por que.
     */
    constructor(update, onChoose, onMissing) {
        this.update = update;
        this.onChoose = onChoose;
        this.onMissing = onMissing;
        this.scope = new CompanySelectorScope();
        /** Qual requisicao ainda pode falar - duas respostas podem cruzar. */
        this.pending = 0;
        this.scope.update = update;
        this.scope.onTermChanged = this.handleTermChanged.bind(this);
        this.scope.onOpen = this.onOpen.bind(this);
        this.scope.onClear = async () => {
            await this.choose(undefined, '');
        };
    }
    /**
     * Restringe a escolha a estas empresas.
     *
     * Sem ida ao servidor: sao duas ou tres, ja em maos, e buscar seria pedir de novo
     * o que a casca acabou de trazer. O campo de busca continua funcionando - ele
     * filtra a lista em vez de consultar.
     *
     * Tira o X junto: num conjunto fechado nao existe "todas". Cada tela fala de UMA
     * empresa, e o servidor recusa agir sem saber qual - um botao de limpar levaria a
     * pessoa direto a uma recusa.
     */
    restrictTo(companies) {
        this.restricted = companies;
        this.scope.clearable = false;
        this.showRestricted(this.scope.term);
    }
    /**
     * Como este seletor se apresenta na tela que o hospeda.
     *
     * O painel o mostra como um botao dentro do banner roxo, sem rotulo; os dois
     * filtros o mostram como campo de formulario, com rotulo em cima e X ao lado. O
     * texto e de quem hospeda porque e a tela que sabe o que esta filtrando.
     */
    describe(label, placeholder) {
        this.scope.label = label;
        this.scope.placeholder = placeholder;
        if (!this.scope.summary) {
            this.scope.summary = placeholder;
        }
    }
    /**
     * Diz ao seletor qual empresa esta escolhida.
     *
     * O NOME vem junto quando quem chama ja o tem. Sem ele, o botao mostraria o id
     * ate a primeira busca responder - e chegando por link (`?empresa=256`) nao ha
     * nome nenhum em maos, entao o proprio seletor vai busca-lo.
     */
    async showSelected(companyId, name) {
        this.selected = companyId;
        this.scope.hasSelection = companyId !== undefined;
        if (companyId === undefined) {
            this.scope.summary = this.scope.placeholder;
            return;
        }
        if (name) {
            this.scope.summary = name;
            return;
        }
        /*
         * Sem `await`: quem chama esta no meio de uma viagem, e o nome e enfeite do
         * botao - nao decide nada da tela. Esperar a rede aqui deixava a face parada,
         * e a empresa INEXISTENTE precisa voltar para "todas" depois que a viagem
         * terminou, e nao no meio dela.
         */
        this.scope.summary = 'Carregando…';
        this.loadName(companyId).catch((caught) => LOG.warn('Could not resolve the selected company name', caught));
    }
    /** O nome de quem chegou pelo endereco, para o botao nao mostrar um numero. */
    async loadName(companyId) {
        try {
            const company = await companyApi.fetchCompany(companyId);
            if (this.selected === companyId) {
                this.scope.summary = company.nomeFantasia || company.razaoSocial;
            }
        }
        catch (caught) {
            LOG.warn('Could not resolve the selected company name', caught);
            if (this.selected !== companyId) {
                return;
            }
            /*
             * A empresa NAO EXISTE (o servidor responde 400 "Empresa nao encontrada").
             * Antes o botao mostrava "Empresa 256" e a tela, os numeros de uma empresa
             * vazia - sem dizer nada (B-40, 16/10/2026). Agora volta para "todas", como
             * o X, e o hospedeiro avisa. Falha de REDE nao e isso: ai a empresa pode
             * existir, e fica o numero.
             */
            const status = caught.status;
            if (status === 400 || status === 404) {
                /*
                 * Uma volta do laco de eventos antes de escolher "todas". O hospedeiro
                 * chama `showSelected` e, logo em seguida, ainda na mesma tarefa, pede os
                 * dados da empresa do endereco. Se a recusa chegasse antes desse pedido,
                 * "todas" seria a carga mais VELHA, e a da empresa inexistente venceria.
                 */
                await new Promise((resolve) => setTimeout(resolve, 0));
                if (this.selected !== companyId) {
                    return;
                }
                await this.choose(undefined, '');
                this.onMissing?.();
                return;
            }
            this.scope.summary = `Empresa ${companyId}`;
        }
    }
    async onOpen() {
        await this.search(this.scope.term);
    }
    handleTermChanged(value) {
        this.scope.term = value;
        if (this.timer) {
            clearTimeout(this.timer);
        }
        this.timer = setTimeout(() => {
            void this.search(value);
        }, DEBOUNCE_MS);
    }
    async search(term) {
        if (this.restricted) {
            this.showRestricted(term);
            return;
        }
        const token = ++this.pending;
        this.scope.loading = true;
        try {
            const page = await companyApi.searchCompanies(term, 0, PAGE_SIZE);
            // Quem perdeu a corrida fica calado: digitar rapido dispara varias, e a
            // que volta por ultimo nao e necessariamente a do termo em tela.
            if (token !== this.pending) {
                return;
            }
            this.scope.total = page.count ?? page.elements.length;
            this.scope.options = [this.buildAll(), ...page.elements.map((company) => this.buildOption(company))];
        }
        catch (caught) {
            if (token === this.pending) {
                LOG.warn('Could not search companies', caught);
                this.scope.options = [this.buildAll()];
                this.scope.total = 0;
            }
        }
        finally {
            if (token === this.pending) {
                this.scope.loading = false;
            }
        }
    }
    /**
     * As opcoes do conjunto fechado, filtradas pelo termo.
     *
     * Sem a opcao "todas": quem administra duas empresas nao tem "todas" - cada tela
     * fala de UMA, e o servidor recusa agir sem saber qual.
     */
    showRestricted(term) {
        const busca = term.trim().toLowerCase();
        const visiveis = (this.restricted ?? []).filter((company) => {
            if (!busca)
                return true;
            const nome = `${company.nomeFantasia ?? ''} ${company.razaoSocial ?? ''}`.toLowerCase();
            return nome.includes(busca) || (company.cnpj ?? '').includes(busca);
        });
        this.scope.total = visiveis.length;
        this.scope.options = visiveis.map((company) => this.buildOption(company));
        this.scope.loading = false;
    }
    /** "Todas as Empresas" e uma opcao, e nao um estado a parte: limpar e escolher. */
    buildAll() {
        const scope = new CompanyOptionScope();
        scope.identity = 'todas';
        scope.label = this.scope.placeholder;
        scope.value = '';
        scope.current = this.selected === undefined;
        scope.onSelect = async () => {
            await this.choose(undefined, '');
        };
        scope.update = this.update;
        return scope;
    }
    buildOption(company) {
        const label = company.nomeFantasia || company.razaoSocial;
        const scope = new CompanyOptionScope();
        scope.identity = String(company.id);
        scope.label = label;
        // A empresa bloqueada continua no seletor do administrador geral, que segue
        // vendo os dados dela (decisao de 08/10/2026) - mas marcada.
        scope.note = [company.profissao, company.active === false ? 'Acesso bloqueado' : '']
            .filter(Boolean)
            .join(' · ');
        scope.value = String(company.id);
        scope.current = String(this.selected ?? '') === String(company.id);
        scope.onSelect = async () => {
            await this.choose(company.id, label);
        };
        scope.update = this.update;
        return scope;
    }
    async choose(companyId, label) {
        this.selected = companyId;
        this.scope.hasSelection = companyId !== undefined;
        this.scope.summary = label || this.scope.placeholder;
        this.scope.term = '';
        this.scope.revision += 1;
        await this.onChoose(companyId);
    }
}
//# sourceMappingURL=company-selector.presenter.js.map